import jsPDF from 'jspdf';
import autoTable from "jspdf-autotable";
import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReauditCAPModal from '../../../Components/ChecklistComponents/ReauditCAPModal';

const BASE_URL = process.env.REACT_APP_API_URL;


const ReAuditDetails = () => {
  const [checklistData, setChecklistData] = useState(null);
  const [sections, setSections] = useState([]);
  const [progress, setProgress] = useState({
    totalQuestions: 0,
    answeredQuestions: 0,
    progressPercentage: 0
  });
  const [isCompleted, setIsCompleted] = useState(false);
  const [showCAPModal, setShowCAPModal] = useState(false);
  const [capQuestions, setCapQuestions] = useState([]);
  const [profileImage, setProfileImage] = useState(null); // New state for profile image
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingCAP, setIsProcessingCAP] = useState(false);
  const [isCompletingAudit, setIsCompletingAudit] = useState(false);
  const [isReportGenerating, setIsReportGenerating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { checklist } = location.state || {};
  const auditorData = JSON.parse(localStorage.getItem("auditor"));

  const notify = (message, type = 'success') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  useEffect(() => {
    if (checklist?.id) {
      setIsLoading(true);
      Promise.all([
        fetchChecklistData(),
        fetchProgress()
      ])
      .catch(error => {
        console.error('Error loading re-audit details:', error);
        notify('Failed to load re-audit details', 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    }
  }, [checklist]);

  useEffect(() => {
    if (progress.totalQuestions > 0) {
      setIsCompleted(progress.answeredQuestions === progress.totalQuestions);
    }
  }, [progress]);

  // Add this new effect to fetch the profile image
  useEffect(() => {
    const fetchAuditorProfile = async () => {
      if (auditorData && auditorData.id) {
        try {
          const response = await fetch(`${BASE_URL}/get-auditor/${auditorData.id}`);
          if (response.ok) {
            const profileData = await response.json();
            if (profileData.profileImage) {
              setProfileImage(`${BASE_URL}${profileData.profileImage}`);
            }
          }
        } catch (error) {
          console.error("Failed to load auditor profile image:", error);
          // Continue without profile image
        }
      }
    };

    fetchAuditorProfile();
  }, [auditorData, BASE_URL]);

  const fetchChecklistData = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-reaudit-details/${checklist.id}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch re-audit details');
      
      const data = await response.json();
      setChecklistData(data);
      
      if (data.sections) {
        setSections(JSON.parse(data.sections));
      }
    } catch (error) {
      console.error('Error:', error);
      notify('Failed to load re-audit details', 'error');
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-reaudit-progress/${checklist.original_checklist_id}/${checklist.id}/${auditorData.id}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch progress');
      
      const data = await response.json();
      setProgress(data);
    } catch (error) {
      console.error('Error:', error);
      notify('Failed to load progress data', 'error');
    }
  };

  const fetchCapQuestions = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-reaudit-cap-questions/${checklist.id}/${checklist.branch_id}/${auditorData.id}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch CAP questions');
      }

      const data = await response.json();
      setCapQuestions(data);
    } catch (error) {
      console.error('Error:', error);
      notify('Failed to fetch CAP questions', 'error');
    }
  };

  const handleProceed = async () => {
    try {
      if (checklist.status === 'Completed') {
        navigate('/ReAuditSections', {
          state: { checklist }
        });
        return;
      }

      const response = await fetch(`${BASE_URL}/update-reaudit-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reauditId: checklist.id,
          status: 'In progress'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      const result = await response.json();
      
      if (result.success) {
        notify('Status updated successfully');
        navigate('/ReAuditSections', {
          state: { 
            checklist: {
              ...checklist,
              status: 'In progress'
            }
          }
        });
      }
    } catch (error) {
      console.error('Error:', error);
      notify(error.message, 'error');
    }
  };

  const handleCAP = async () => {
    try {
      setIsProcessingCAP(true);
      await fetchCapQuestions();
      setShowCAPModal(true);
    } catch (error) {
      console.error('Error:', error);
      notify(error.message, 'error');
    } finally {
      setIsProcessingCAP(false);
    }
  };

  const organizeCapQuestions = (questions) => {
    return questions.reduce((acc, question) => {
      const section = question.section || 'Other';
      if (!acc[section]) {
        acc[section] = {
          questions: [],
          subsections: {}
        };
      }
      
      if (question.subsection) {
        if (!acc[section].subsections[question.subsection]) {
          acc[section].subsections[question.subsection] = [];
        }
        acc[section].subsections[question.subsection].push(question);
      } else {
        acc[section].questions.push(question);
      }
      
      return acc;
    }, {});
  };

  const handleSaveCAP = async (capActions, action = 'saveOnly') => {
    try {
      // Skip saving CAP actions if createReauditOnly action
      if (action !== 'createReauditOnly') {
        // First save the CAP actions
        for (const [questionId, actionData] of Object.entries(capActions)) {
          const response = await fetch(`${BASE_URL}/save-reaudit-cap-action`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              reauditChecklistId: checklist.id,
              questionId,
              branchId: checklist.branch_id,
              auditorId: auditorData.id,
              responsiblePerson: actionData.responsiblePerson,
              dueDate: actionData.dueDate
            })
          });

          if (!response.ok) {
            throw new Error(`Failed to save CAP action for question ${questionId}`);
          }
        }

        // Show success notification for CAP actions save
        notify('CAP actions saved successfully');
      }
      
      // For both direct re-audit and save+re-audit, create re-audit
      if (action === 'createReauditOnly' || action === 'saveAndCreateReaudit') {
        // Check if re-audit already exists
        const checkResponse = await fetch(
          `${BASE_URL}/check-next-reaudit-exists/${checklist.checklist_id}/${checklist.id}`
        );
        
        if (!checkResponse.ok) {
          throw new Error('Failed to check if re-audit exists');
        }
        
        const checkData = await checkResponse.json();
        
        if (checkData.exists) {
          notify('Re-audit already exists for this checklist', 'warning');
        } else {
          // Create re-audit checklist
          const createResponse = await fetch(`${BASE_URL}/create-next-reaudit-checklist`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              reauditChecklistId: checklist.id,
              originalChecklistId: checklist.original_checklist_id,
              assignedChecklistId: checklist.assigned_checklist_id,
              branchId: checklist.branch_id,
              auditorId: auditorData.id,
              checklistName: checklist.checklist_name,
              BranchCode: checklist.BranchCode,
              branches: checklist.branches,
              status: checklist.status
            })
          });
          
          if (!createResponse.ok) {
            throw new Error('Failed to create re-audit checklist');
          }
          
          const createData = await createResponse.json();
          
          if (createData.success) {
            notify('Re-audit checklist created successfully');
            // Refresh the page to show the updated status
            window.location.reload();
          } else {
            throw new Error(createData.message || 'Failed to create re-audit checklist');
          }
        }
      }
      
      // Close modal
      setShowCAPModal(false);
      
    } catch (error) {
      console.error('Error:', error);
      notify(`Failed: ${error.message}`, 'error');
    }
  };

  const handleComplete = async () => {
    try {
      setIsCompletingAudit(true);
      const response = await fetch(`${BASE_URL}/update-reaudit-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reauditId: checklist.id,
          status: 'Completed'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      const result = await response.json();
      
      if (result.success) {
        notify('Re-audit completed successfully');
        // Update local state
        checklist.status = 'Completed';
        
        // Show a success message and navigate after a short delay
        setTimeout(() => {
          // Navigate to the ReAuditChecklist page
          navigate('/ReAuditChecklist');
        }, 3000); // 1.5 second delay to show the success notification
      } else {
        throw new Error(result.message || 'Failed to complete re-audit');
      }
    } catch (error) {
      console.error('Error:', error);
      notify(error.message, 'error');
    } finally {
      setIsCompletingAudit(false);
    }
  };

    const handleReport = async () => {
    try {
      setIsReportGenerating(true);
      // Replace axios with fetch
      const response = await fetch(
        `${BASE_URL}/get-reaudit-report-data/${checklist.BranchCode}/${checklist.assigned_checklist_id}/${checklist.id}/${checklist.branch_id}/${auditorData.id}`
      );
  
      if (!response.ok) {
        throw new Error('Failed to fetch report data');
      }
  
      const reportData = await response.json();
      
      // Generate PDF using jsPDF
      generatePDF(reportData);
  
    } catch (error) {
      console.error('Error:', error);
      notify('Failed to generate report', 'error');
    } finally {
      setIsReportGenerating(false);
    }
  };

const generatePDF = (data) => {
  try {
    // Create a new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    
    // Manually register the plugin if needed
    if (typeof doc.autoTable !== 'function') {
      doc.autoTable = autoTable;
    }
    
    const { checklist, auditor,supplier, branch, responses, summary, sectionScores, sections } = data;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let y = margin;
    
    // Color scheme for a professional look
    const colors = {
      primary: [41, 128, 185],       // Blue
      secondary: [52, 73, 94],       // Dark blue-gray
      accent: [39, 174, 96],         // Green
      light: [236, 240, 241],        // Light gray
      text: [44, 62, 80],            // Dark gray
      textLight: [127, 140, 141],    // Medium gray
      border: [189, 195, 199]        // Light border
    };
    
    // Helper functions
    const addHeader = (text, fontSize = 16) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontSize);
      doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
      doc.text(text, pageWidth / 2, y, { align: 'center' });
      y += fontSize / 2;
      return y;
    };
    
    const addDivider = (width = pageWidth - (margin * 2), color = colors.border) => {
      doc.setDrawColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, y, width + margin, y);
      y += 4;
      return y;
    };
    
    // Function to check if a value is valid for display
    const isValidValue = (value) => {
      return value !== null && value !== undefined && !isNaN(value) && value !== 0;
    };
    
    // Reorganize section scores to show hierarchy
    const hierarchicalSections = {};

    // Process section names to extract hierarchy
    Object.keys(sectionScores).forEach(fullSectionName => {
      const section = sectionScores[fullSectionName];
      
      // Skip sections with null/undefined data
      if (!section || section.total === 0) return;
      
      // Check for multiple levels using ">"
      const parts = fullSectionName.split(' > ');
      
      if (parts.length === 1) {
        // Main section
        hierarchicalSections[parts[0]] = hierarchicalSections[parts[0]] || {
          name: parts[0],
          level: 0,
          score: section.score,
          total: section.total,
          questions: section.questions,
          subsections: {},
          complianceRate: Math.round((section.score / section.total) * 100)
        };
      } 
      else if (parts.length === 2) {
        // Second level (subsection)
        // Ensure parent exists
        hierarchicalSections[parts[0]] = hierarchicalSections[parts[0]] || {
          name: parts[0],
          level: 0,
          score: 0,
          total: 0,
          questions: 0,
          subsections: {},
          complianceRate: 0
        };
        
        // Add subsection
        hierarchicalSections[parts[0]].subsections[parts[1]] = {
          name: parts[1],
          level: 1,
          score: section.score,
          total: section.total,
          questions: section.questions,
          subsections: {},
          complianceRate: Math.round((section.score / section.total) * 100)
        };
        
        // Update parent totals
        hierarchicalSections[parts[0]].score += section.score;
        hierarchicalSections[parts[0]].total += section.total;
        hierarchicalSections[parts[0]].questions += section.questions;
        
        // Recalculate parent compliance rate
        if (hierarchicalSections[parts[0]].total > 0) {
          hierarchicalSections[parts[0]].complianceRate = Math.round(
            (hierarchicalSections[parts[0]].score / hierarchicalSections[parts[0]].total) * 100
          );
        }
      } 
      else if (parts.length >= 3) {
        // Third level or deeper (sub-subsection)
        // Ensure parent and grandparent exist
        hierarchicalSections[parts[0]] = hierarchicalSections[parts[0]] || {
          name: parts[0],
          level: 0,
          score: 0,
          total: 0,
          questions: 0,
          subsections: {},
          complianceRate: 0
        };
        
        hierarchicalSections[parts[0]].subsections[parts[1]] = 
          hierarchicalSections[parts[0]].subsections[parts[1]] || {
            name: parts[1],
            level: 1,
            score: 0,
            total: 0,
            questions: 0,
            subsections: {},
            complianceRate: 0
          };
        
        // Add sub-subsection
        hierarchicalSections[parts[0]].subsections[parts[1]].subsections[parts[2]] = {
          name: parts[2],
          level: 2,
          score: section.score,
          total: section.total,
          questions: section.questions,
          complianceRate: Math.round((section.score / section.total) * 100)
        };
        
        // Update parent and grandparent totals
        hierarchicalSections[parts[0]].score += section.score;
        hierarchicalSections[parts[0]].total += section.total;
        hierarchicalSections[parts[0]].questions += section.questions;
        
        hierarchicalSections[parts[0]].subsections[parts[1]].score += section.score;
        hierarchicalSections[parts[0]].subsections[parts[1]].total += section.total;
        hierarchicalSections[parts[0]].subsections[parts[1]].questions += section.questions;
        
        // Recalculate parent and grandparent compliance rates
        if (hierarchicalSections[parts[0]].total > 0) {
          hierarchicalSections[parts[0]].complianceRate = Math.round(
            (hierarchicalSections[parts[0]].score / hierarchicalSections[parts[0]].total) * 100
          );
        }
        
        if (hierarchicalSections[parts[0]].subsections[parts[1]].total > 0) {
          hierarchicalSections[parts[0]].subsections[parts[1]].complianceRate = Math.round(
            (hierarchicalSections[parts[0]].subsections[parts[1]].score / 
             hierarchicalSections[parts[0]].subsections[parts[1]].total) * 100
          );
        }
      }
    });
    
    // Add company logo if available
    // If you have a logo, uncomment and adjust this code:
    /*
    try {
      doc.addImage(
        'logo.png', // Replace with your logo path or URL
        'PNG',
        margin,
        y,
        40,
        15
      );
      y += 20;
    } catch (err) {
      console.log('Logo not available', err);
      // Continue without logo
    }
    */
    
    // // Title with professional styling
    // doc.setFillColor(colors.light[0], colors.light[1], colors.light[2]);
    // doc.rect(0, 0, pageWidth, 25, 'F');
    // doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
    // // doc.setLineWidth(0.5);
    // // doc.line(0, 40, pageWidth, 40);
    
    // doc.setFont('helvetica', 'bold');
    // doc.setFontSize(22);
    // doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
    // doc.text('AUDIT REPORT', pageWidth / 2, 15, { align: 'center' });

    y = 15; // Start even higher up
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18); // Smaller size
    doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
    doc.text('AUDIT REPORT', margin, y);

    y=20;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12); // Smaller size
    doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
    doc.text(branch.branchName, margin, y);
    
    // Reset y position after header
    y = 30;
    
    // Report details section with box
    doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
    doc.setLineWidth(0.5);
    doc.roundedRect(margin, y, pageWidth - (margin * 2), 45, 2, 2, 'S');
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
    doc.text('Supplier:', margin + 5, y + 10);
    doc.text('Checklist:', margin + 5, y + 20);
    doc.text('Auditor:', margin + 5, y + 30);
    doc.text('Date:', margin + 5, y + 40);
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    doc.text(`${data.supplier?.firstName} ${data.supplier?.lastName}`, margin + 30, y + 10);
    doc.text(checklist.checklist_name, margin + 30, y + 20);
    doc.text(`${auditor.firstName} ${auditor.lastName}`, margin + 30, y + 30);
    
    const currentDate = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    doc.text(currentDate, margin + 30, y + 40);
    
    y += 55;
    
    // Executive Summary with professional styling
    y = addHeader('EXECUTIVE SUMMARY');
    addDivider();
    y += 5;
    

    
    // Summary metrics in a professional box layout
    const boxWidth = (pageWidth - (margin * 2) - 15) / 4;
    const boxHeight = 30;
    
    // Define metrics
    const metrics = [
      { label: 'Overall Score', value: `${Math.round(summary.avgScore * 10) / 10}/10` },
      { label: 'Completed Items', value: `${summary.completedQuestions}/${summary.totalQuestions}` },
      { label: 'CAP Required', value: summary.capRequired.toString() },
      { label: 'Compliance Rate', value: `${Math.round((summary.avgScore / 10) * 100)}%` }
    ];
    
    // Draw metrics boxes
    metrics.forEach((metric, index) => {
      const x = margin + (index * (boxWidth + 5));
      
      // Box with slight gradient effect
      doc.setFillColor(250, 250, 252);
      doc.roundedRect(x, y, boxWidth, boxHeight, 2, 2, 'F');
      doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
      doc.roundedRect(x, y, boxWidth, boxHeight, 2, 2, 'S');
      
      // Value with larger, bold font
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      doc.text(metric.value, x + boxWidth/2, y + 15, { align: 'center' });
      
      // Label with smaller font below
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
      doc.text(metric.label, x + boxWidth/2, y + 24, { align: 'center' });
    });
    
    y += boxHeight + 15;
    
    // Section Scores with hierarchical organization
    y = addHeader('SECTION PERFORMANCE', 14);
    y = addDivider();
    
    // Prepare table header and rows
    const sectionTableHead = [
      ['Section', 'Score', 'Questions', 'Compliance']
    ];
    
    // Create a single body array that will contain ALL rows
    const sectionTableBody = [];
    
    // Build hierarchical rows with proper numbering
    let sectionIndex = 1;
    
    Object.values(hierarchicalSections).forEach(section => {
      // Add main section row (level 0)
      sectionTableBody.push([
        { content: `${sectionIndex}. ${section.name}`, styles: { fontStyle: 'bold' } },
        {
          content: isValidValue(section.score) && isValidValue(section.total) ? 
            `${section.score}/${section.total}` : '',
          styles: { halign: 'center', fontStyle: 'bold' }
        },
        {
          content: isValidValue(section.questions) ? section.questions.toString() : '',
          styles: { halign: 'center', fontStyle: 'bold' }
        },
        {
          content: isValidValue(section.complianceRate) ? `${section.complianceRate}%` : '',
          styles: { halign: 'center', fontStyle: 'bold' }
        }
      ]);
      
      // Add subsection rows (level 1) with proper numbering
      let subsectionIndex = 1;
      Object.values(section.subsections).forEach(subsection => {
        sectionTableBody.push([
          { 
            content: `${sectionIndex}.${subsectionIndex}. ${subsection.name}`, 
            styles: { fontStyle: 'bold', fontSize: 8 }
          },
          {
            content: isValidValue(subsection.score) && isValidValue(subsection.total) ? 
              `${subsection.score}/${subsection.total}` : '',
            styles: { fontStyle: 'bold',halign: 'center' }
          },
          {
            content: isValidValue(subsection.questions) ? subsection.questions.toString() : '',
            styles: { fontStyle: 'bold',halign: 'center' }
          },
          {
            content: isValidValue(subsection.complianceRate) ? `${subsection.complianceRate}%` : '',
            styles: { fontStyle: 'bold',halign: 'center' }
          }
        ]);
        
       
        subsectionIndex++;
      });
      sectionIndex++;
    });
    
    // Draw section scores table with proper styling - ONE TABLE with one header
    // This ensures only one header row appears
    autoTable(doc, {
      head: sectionTableHead,
      body: sectionTableBody,
      startY: y,
      headStyles: {
        fillColor: colors.secondary,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
        halign: 'center',
        valign: 'middle'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: {
        0: { 
          cellWidth: pageWidth - (margin * 2) - 90, 
          halign: 'left'
        },
        1: { 
          cellWidth: 30, 
          halign: 'center'
        },
        2: { 
          cellWidth: 30, 
          halign: 'center'
        },
        3: { 
          cellWidth: 30, 
          halign: 'center'
        }
      },
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 9,
        cellPadding: 3,
        valign: 'middle',
        lineColor: [220, 220, 220],
        lineWidth: 0.1
      },
      didParseCell: function(data) {
        // Apply styling to cells
        if (data.section === 'head') {
          if (data.column.index === 0) {
            data.cell.styles.halign = 'left';
          } else {
            data.cell.styles.halign = 'center';
          }
        }
        
        if (data.section === 'body') {
          if (data.column.index === 0) {
            // Apply proper indentation for hierarchical sections
            data.cell.styles.halign = 'left';
            
            // Add indentation based on section level
            if (data.cell.text[0].includes('.')) {
              const dots = (data.cell.text[0].match(/\./g) || []).length;
              if (dots === 1) {
                // Level 1 subsection
                data.cell.styles.cellPadding = {top: 3, right: 3, bottom: 3, left: 10};
              } else if (dots >= 2) {
                // Level 2 or deeper subsection
                data.cell.styles.cellPadding = {top: 3, right: 3, bottom: 3, left: 15};
              }
            }
          } else {
            data.cell.styles.halign = 'center';
          }
        }
      },
      // IMPORTANT: This option prevents headers from repeating on new pages
      showHead: 'firstPage'
    });
    
    // Get the final Y position after the section performance table
    y = doc.lastAutoTable.finalY + 15;
    
    // REMOVE TOP FINDINGS SECTION - Replace with the code below to go directly to the next page
    doc.addPage();
    y = margin;
    
    // Detailed Findings header
    y = addHeader('DETAILED AUDIT FINDINGS', 16);
    doc.setDrawColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    doc.setLineWidth(1);
    doc.line(margin, y - 2, pageWidth - margin, y - 2);
    y += 10;
    
    // Create section counter to manage numbering correctly
    let sectionCounter = 1;
    
    // This will store section numbers by name for cross-referencing
    const sectionNumbering = {};
    
    // Draw custom header rectangle instead of using autoTable header
    // Define column widths - MUST match exactly with data rows
    const colWidths = {
      col0: 10,                             // # column width
      col1: pageWidth - (margin * 2) - 80,  // Question column width
      col2: 45,                             // Response column width
      col3: 25                              // Points column width
    };
    
    // Create a header rectangle with proper styling
    doc.setFillColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
    doc.rect(margin, y, pageWidth - (margin * 2), 10, 'F');
    
    // Add header text with proper positioning to match column widths in data
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    
    // Calculate column positions for text alignment
    const col0Center = margin + (colWidths.col0 / 2);
    const col1Start = margin + colWidths.col0 + 5;
    const col2Start = margin + colWidths.col0 + colWidths.col1 + 5;
    const col3Center = margin + colWidths.col0 + colWidths.col1 + colWidths.col2 + (colWidths.col3 / 2);
    
    // Add header texts with precise positioning
    doc.text('#', col0Center, y + 6, { align: 'center' });
    doc.text('Question', col1Start, y + 6, { align: 'left' });
    doc.text('Response', col2Start, y + 6, { align: 'left' });
    doc.text('Points', col3Center, y + 6, { align: 'center' });
    
    // Move down after header
    y += 15;
    
    // Process sections recursively with proper numbering
    const processSection = async (section, level = 0, parentNumber = '') => {
      let sectionNumber;
      
      if (level === 0) {
        // Main section
        sectionNumber = `${sectionCounter}`;
        sectionNumbering[section.name] = sectionNumber;
        sectionCounter++;
      } else {
        // Subsection - use the parent's number plus its position
        const subSectionIndex = section.parent?.subsections?.findIndex(s => s.name === section.name) + 1 || 1;
        sectionNumber = `${parentNumber}.${subSectionIndex}`;
        sectionNumbering[section.name] = sectionNumber;
      }
      
      // Check if we need a new page
      if (y > pageHeight - 60) {
        doc.addPage();
        y = margin;
      }
      
      // Section header with background
      doc.setFillColor(
        colors.light[0], 
        colors.light[1], 
        colors.light[2]
      );
      
      // Adjust indent based on level
      const indent = level * 5;
      doc.roundedRect(margin + indent, y, pageWidth - (margin * 2) - indent, 10, 1, 1, 'F');
      
      // Section title with number
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10 - level);
      doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
      doc.text(
        `${sectionNumber}. ${section.name}`, 
        margin + 3 + indent, 
        y + 6
      );
      
      y += 15;
      
      // Process questions in this section with optimized spacing
      if (section.questions && section.questions.length > 0) {
        let questionCounter = 1;
        
        for (const questionId of section.questions) {
          const response = responses.find(r => r.question_id === questionId);
          if (!response) continue;
          
          // More aggressive page break check - only break if less than 40mm remains
          // This allows more content to fit on each page
          if (y > pageHeight - 40) {
            doc.addPage();
            y = margin;
          }
          
          // Question data
          const questionText = response.questionText || 'No question text';
          const answer = response.answer || 'No answer';
          const points = response.points || 0;
          const capText = response.cap === 'Yes' ? 'CAP Required' : '';
          
          // Question identifier with simplified format
          const questionIdentifier = `${questionCounter}`;
          
          const pointsColor = points < 5 ? 
            [217, 83, 79] : // Red for low points
            (points < 8 ? [240, 173, 78] : [92, 184, 92]); // Yellow or Green
          
          // Create the question row with reduced padding to save space
          autoTable(doc, {
            startY: y,
            body: [[
              { content: questionIdentifier, styles: { halign: 'center', valign: 'middle' } },
              { content: questionText, styles: { halign: 'left', valign: 'middle' } },
              { content: answer, styles: { halign: 'left', valign: 'middle' } },
              { 
                content: `${points}/10${capText ? '\n' + capText : ''}`,
                styles: {
                  textColor: pointsColor,
                  fontStyle: capText ? 'bold' : 'normal',
                  halign: 'center',
                  valign: 'middle'
                }
              }
            ]],
            theme: 'grid',
            styles: {
              overflow: 'linebreak',
              cellPadding: 3, // REDUCED padding from 5 to 3
              fontSize: 8,
              minCellHeight: 8, // REDUCED from 10 to 8 
              lineColor: [220, 220, 220],
              lineWidth: 0.1
            },
            // Use the same column widths
            columnStyles: {
              0: { cellWidth: colWidths.col0, halign: 'center' },
              1: { cellWidth: colWidths.col1, halign: 'left' },
              2: { cellWidth: colWidths.col2, halign: 'left' },
              3: { cellWidth: colWidths.col3, halign: 'center' }
            },
            margin: { left: margin, right: margin },
            tableLineColor: [220, 220, 220],
            tableLineWidth: 0.1
          });
          
          y = doc.lastAutoTable.finalY;
          
          // Add media files if available but with tighter spacing
          if (response.media_file && response.media_file.trim().length > 0) {
            try {
              // Parse the media_file content
              let mediaFiles = [];
              
              try {
                // First try to parse as JSON
                mediaFiles = JSON.parse(response.media_file);
                // Ensure it's an array
                if (!Array.isArray(mediaFiles)) {
                  mediaFiles = [response.media_file];
                }
                // Remove any empty or null entries
                mediaFiles = mediaFiles.filter(file => file && file.trim().length > 0);
              } catch (err) {
                // If parsing fails, treat as a single filename
                if (response.media_file && response.media_file.trim().length > 0) {
                  mediaFiles = [response.media_file];
                }
              }
          
              // Only proceed if we have valid files
              if (mediaFiles && mediaFiles.length > 0) {
                // Evidence label
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(7);
                doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
                doc.text('Evidence:', margin + 2, y + 5);
                
                // Layout configuration
                const maxImagesPerRow = 3; // Back to 3 images per row for better sizing
                const imageWidth = 35; // Slightly larger for better visibility
                const imageHeight = 25; // Slightly larger for better visibility
                const imageMargin = 3;
                
                let currentX = margin + 5;
                let currentY = y + 7;
                let rowHeight = 0;
                
                // Process each image - better page break handling
                for (let i = 0; i < mediaFiles.length; i++) {
                  // Check for page break BEFORE adding the image
                  if (currentY + imageHeight > pageHeight - 30) {
                    doc.addPage();
                    y = margin;
                    currentY = y + 5;
                    currentX = margin + 5;
                    
                    // Add the 'Evidence:' label on the new page
                    doc.setFont('helvetica', 'italic');
                    doc.setFontSize(7);
                    doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
                    doc.text('Evidence (continued):', margin + 2, currentY - 2);
                    currentY += 3;
                  }
                  
                  // Start a new row if needed
                  if (currentX + imageWidth > pageWidth - margin) {
                    currentX = margin + 5;
                    currentY += rowHeight + imageMargin;
                    rowHeight = 0;
                  }
                  
                  try {
                    // Construct the proper image URL with better error handling
                    let imageUrl = '';
                    const fileName = String(mediaFiles[i]);
                    
                    if (fileName.startsWith('http') || fileName.startsWith('https')) {
                      imageUrl = fileName;
                    } else if (fileName.startsWith('data:')) {
                      imageUrl = fileName;
                    } else {
                      // Handle both relative and absolute paths
                      if (fileName && fileName.trim().length > 0) {
                        imageUrl = `${BASE_URL}/uploads/audit-media/${fileName}`;
                      } else {
                        throw new Error("Invalid file name");
                      }
                    }
                    
                    // Determine file format from extension
                    let format = 'JPEG';
                    if (fileName.toLowerCase().endsWith('.png')) format = 'PNG';
                    if (fileName.toLowerCase().endsWith('.gif')) format = 'GIF';
                    if (fileName.toLowerCase().endsWith('.webp')) format = 'WEBP';
                    
                    // Add the image to the PDF
                    doc.addImage(
                      imageUrl,
                      format,
                      currentX,
                      currentY,
                      imageWidth,
                      imageHeight,
                      undefined,
                      'FAST'
                    );
                    
                    // Add subtle border around image
                    doc.setDrawColor(180, 180, 180);
                    doc.setLineWidth(0.3);
                    doc.rect(currentX, currentY, imageWidth, imageHeight);
                    
                    // Update row height if this image is taller
                    rowHeight = Math.max(rowHeight, imageHeight);
                    
                    // Move to next position
                    currentX += imageWidth + imageMargin;
                    
                  } catch (imgErr) {
                    console.error(`Error adding image ${mediaFiles[i]}:`, imgErr);
                    
                    // Draw a placeholder for failed images
                    doc.setDrawColor(200, 200, 200);
                    doc.setFillColor(245, 245, 245);
                    doc.rect(currentX, currentY, imageWidth, imageHeight, 'FD');
                    
                    // Add error icon
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(12);
                    doc.setTextColor(180, 180, 180);
                    doc.text('⚠️', currentX + imageWidth/2, currentY + imageHeight/2 - 6, {
                      align: 'center'
                    });
                    
                    // Add error text
                    doc.setFontSize(6);
                    doc.setTextColor(150, 150, 150);
                    doc.text('Image not available', currentX + imageWidth/2, currentY + imageHeight/2 + 2, {
                      align: 'center'
                    });
                    
                    // Move to next position
                    currentX += imageWidth + imageMargin;
                    rowHeight = Math.max(rowHeight, imageHeight);
                  }
                }
                
                // Update y position after all images
                y = currentY + rowHeight + 5;
              }
            } catch (err) {
              console.error(`Error processing media files:`, err);
            }
          } else {
            // Only add a tiny bit of space if no images
            y += 2;
          }
          
          // Add remarks with more compact styling
          if (response.remarks && response.remarks.trim()) {
            // Draw remarks with reduced spacing
            doc.setFillColor(250, 250, 250);
            
            const remarkText = doc.splitTextToSize(
              response.remarks, 
              pageWidth - (margin * 2) - 15
            );
            // Calculate height more efficiently - less padding
            const remarkHeight = remarkText.length * 3 + 6;
            
            doc.roundedRect(margin + 2, y, pageWidth - (margin * 2) - 4, remarkHeight, 1, 1, 'F');
            
            // Smaller remarks label
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(6);
            doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
            doc.text('Remarks:', margin + 5, y + 4);
            
            // Remarks content with less spacing
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
            doc.text(response.remarks, margin + 5, y + 9, { 
              maxWidth: pageWidth - (margin * 2) - 10 
            });
            
            y += remarkHeight + 2;
          }
          
          // Minimal spacing after each question
          y += 2; // REDUCED from 5
          questionCounter++;
          
          // Smart page management - check if next question would fit
          // If not much space left but not quite triggering a page break,
          // and we're past 80% of page height, just do the page break now
          const nextQuestionEstimatedHeight = 15; // Estimate height of a basic question
          if (y > (pageHeight - 45) && y > (pageHeight * 0.8)) {
            doc.addPage();
            y = margin;
          }
        }
      }
      
      // Process subsections with correct numbering
      if (section.subsections && section.subsections.length > 0) {
        for (let i = 0; i < section.subsections.length; i++) {
          const subsection = section.subsections[i];
          subsection.parent = section; // Store parent reference for numbering
          
          await processSection(subsection, level + 1, sectionNumber);
        }
      }
    };
    
    // Process all sections sequentially
    const processSectionsSequentially = async () => {
      for (const section of sections) {
        await processSection(section);
      }
      
      // Add page numbers and auditor name to all pages
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        
        // Add footer area with line
        doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
        doc.setLineWidth(0.5);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
        
        // Add auditor name on left
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
        doc.text(
          `Auditor: ${auditor.firstName} ${auditor.lastName}`,
          margin,
          pageHeight - 8
        );
        
        // Add page numbers in center
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
        
        // Add date on right
        doc.text(
          currentDate,
          pageWidth - margin,
          pageHeight - 8,
          { align: 'right' }
        );
      }
      
      // Save PDF with formatted name
      const formattedDate = new Date().toISOString().split('T')[0];
      const safeBranchName = branch.branchName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      
      doc.save(`audit-report-${safeBranchName}-${formattedDate}.pdf`);
      notify('Report successfully generated!', 'success');
    };
    
    // Execute the async function
    processSectionsSequentially().catch(error => {
      console.error("PDF Generation Error:", error);
      notify('Error generating PDF report', 'error');
    });
    
  } catch (error) {
    console.error("PDF Generation Error:", error);
    notify('Error generating PDF report', 'error');
  }
};

  const CircularProgress = ({ percentage, size = 120 }) => {
    const strokeWidth = 12;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;

    // Get color based on percentage
    const getProgressColor = (percent) => {
      if (percent >= 100) return '#198754'; // success/green
      if (percent >= 75) return '#0d6efd';  // primary/blue
      if (percent >= 50) return '#ffc107';  // warning/yellow
      if (percent >= 25) return '#fd7e14';  // orange
      return '#dc3545';                      // danger/red
    };

    const progressColor = getProgressColor(percentage);

    return (
      <div className="circular-progress" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle
            className="progress-bg"
            stroke="#e9ecef"
            strokeWidth={strokeWidth}
            fill="none"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <circle
            className="progress-bar"
            stroke={progressColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            fill="none"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <text
            x="50%"
            y="50%"
            dy=".1em"
            textAnchor="middle"
            dominantBaseline="middle"
            className="progress-text-large"
            style={{ fill: progressColor }}
          >
            {percentage}%
          </text>
        </svg>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Container fluid className="py-5">
        <div className="text-center py-5">
          <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-primary fw-bold mb-1">Loading Re-Audit Details</p>
          <p className="text-muted">Please wait while we fetch your re-audit data...</p>
        </div>
      </Container>
    );
  }

  if (!checklist) {
    return (
      <Container className="text-center py-5">
        <h3>No checklist data available</h3>
      </Container>
    );
  }

  return (
    <>
      <Container fluid className="py-4 px-md-5">
        <ToastContainer />
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={9}>
            <Card className="shadow-lg border-0">
              {/* Header */}
              <Card.Header className="bg-primary text-white py-4">
                <Row className="align-items-center">
                  <Col>
                    <h3 className="mb-0 fw-bold">Re-Audit Details</h3>
                  </Col>
                  <Col xs="auto">
                    <Badge 
                      bg={
                        checklist.status === 'Completed' ? 'success' :
                        checklist.status === 'In progress' ? 'warning' : 'danger'
                      }
                      className="px-3 py-2 rounded-pill"
                    >
                      {checklist.status.toUpperCase()}
                    </Badge>
                  </Col>
                </Row>
              </Card.Header>

              {/* Body */}
              <Card.Body className="p-4">
                <Row className="g-4">
                  {/* Checklist Name */}
                  <Col xs={12} md={6}>
                    <div className="p-3 bg-light rounded-3 h-100">
                      <h6 className="text-muted text-uppercase small mb-2">Checklist Name</h6>
                      <p className="h5 mb-0">{checklist.checklist_name}</p>
                    </div>
                  </Col>

                  {/* Branch */}
                  <Col xs={12} md={6}>
                    <div className="p-3 bg-light rounded-3 h-100">
                      <h6 className="text-muted text-uppercase small mb-2">Branch</h6>
                      <p className="h5 mb-0">{checklist.branches}</p>
                    </div>
                  </Col>

                  {/* Description */}
                  <Col xs={12}>
                    <div className="p-3 bg-light rounded-3">
                      <h6 className="text-muted text-uppercase small mb-2">Description</h6>
                      <p className="h5 mb-0">{checklistData?.original_description || 'N/A'}</p>
                    </div>
                  </Col>

                  {/* Audited By */}
                  <Col xs={12}>
                    <div className="p-3 bg-light rounded-3 h-100">
                      <h6 className="text-muted text-uppercase small mb-2">Audited By</h6>
                      <div className="d-flex align-items-center">
                        {profileImage ? (
                          <div className="avatar-container me-2">
                            <img
                              src={profileImage}
                              alt={`${auditorData.firstName} ${auditorData.lastName}`}
                              className="profile-image"
                              style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                            />
                          </div>
                        ) : (
                          <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" 
                              style={{ width: '40px', height: '40px' }}>
                            <span className="h5 mb-0">{auditorData.firstName.charAt(0)}</span>
                          </div>
                        )}
                        <span className="h5 mb-0">{`${auditorData.firstName} ${auditorData.lastName}`}</span>
                      </div>
                    </div>
                  </Col>

                  
                  
                </Row>

                <Row className="mt-4">
                  <Col xs={12}>
                    <div className="p-4 bg-light rounded-3 ">
                      <Row className="align-items-center">
                        <Col xs={12} md="auto" className="text-center mb-3 mb-md-0">
                          <CircularProgress percentage={progress.progressPercentage} />
                        </Col>
                        <Col>
                          <h6 className="text-muted text-uppercase small mb-3">Progress Overview</h6>
                          <div className="d-flex flex-wrap gap-4">
                            <div>
                              <p className="h2 mb-0 text-primary">{progress.answeredQuestions}</p>
                              <small className="text-muted">Questions Answered</small>
                            </div>
                            <div>
                              <p className="h2 mb-0">{progress.totalQuestions}</p>
                              <small className="text-muted">Total Questions</small>
                            </div>
                            <div>
                              <p className="h2 mb-0 text-warning">{progress.totalQuestions - progress.answeredQuestions}</p>
                              <small className="text-muted">Remaining</small>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Card.Body>

              {/* Footer */}
              <Card.Footer className="bg-light py-4">
                <Row className="justify-content-center g-3">
                  <Col xs={12} sm={6} md={3}>
                    <Button 
                      variant="primary" 
                      onClick={handleProceed}
                      className="w-100 d-flex align-items-center justify-content-center py-2"
                      
                    >
                      <i className="fas fa-play-circle me-2"></i>
                      {checklist.status === 'Completed' ? 'View' : 'Proceed'}
                    </Button>
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <Button 
                      variant="info" 
                      onClick={handleCAP}
                      className="w-100 d-flex align-items-center justify-content-center py-2"
                      disabled={isProcessingCAP || checklist.status !== 'Completed'} // Add condition to enable only when completed
                    >
                      {isProcessingCAP ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                          <span>Loading...</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-tasks me-2"></i>
                          CAP
                        </>
                      )}
                    </Button>
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <Button 
                      variant="success" 
                      onClick={handleComplete}
                      className="w-100 d-flex align-items-center justify-content-center py-2"
                      disabled={!isCompleted || checklist.status === 'Completed' || isCompletingAudit}
                    >
                      {isCompletingAudit ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                          <span>Completing...</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle me-2"></i>
                          Complete
                        </>
                      )}
                    </Button>
                  </Col>
                    <Col xs={12} sm={6} md={3}>
                    <Button 
                      variant="secondary" 
                      onClick={handleReport}
                      className="w-100 d-flex align-items-center justify-content-center py-2"
                      // Only disable if the status is not 'Completed' 
                      disabled={checklist.status !== 'Completed' || isReportGenerating}
                    >
                      {isReportGenerating ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-file-alt me-2"></i>
                          Report
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
              </Card.Footer>
            </Card>
          </Col>
        </Row>

        <style>
          {`
            .btn {
              transition: all 0.2s;
            }
            .btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            }
            
            .circular-progress {
              position: relative;
            }
            
            .progress-bg {
              transform: rotate(-90deg);
              transform-origin: 50% 50%;
            }
            
            .progress-bar {
              transform: rotate(-90deg);
              transform-origin: 50% 50%;
              transition: stroke-dashoffset 0.35s, stroke 0.35s;
            }
            
            .progress-text-large {
              font-size: 24px;
              font-weight: 700;
              transition: fill 0.35s;
            }
            
            .progress-text-small {
              font-size: 14px;
              fill: #6c757d;
            }
            
            @media (max-width: 768px) {
              .progress-stats {
                justify-content: center;
                text-align: center;
              }
            }
            
            .btn:disabled {
              opacity: 0.65;
              transform: none !important;
              box-shadow: none !important;
              cursor: not-allowed;
            }
            
            .btn:disabled:hover {
              transform: none !important;
              box-shadow: none !important;
            }
            
            /* Add these new styles for the avatar */
            .avatar-container {
              width: 40px;
              height: 40px;
              border-radius: 50%;
              overflow: hidden;
              display: flex;
              align-items: center;
              justify-content: center;
              background-color: #f0f0f0;
              border: 1px solid rgba(0, 0, 0, 0.1);
            }
            
            .profile-image {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            .spinner-grow {
              animation-duration: 1.5s;
            }
            
            .text-primary.fw-bold {
              font-size: 1.2rem;
            }
            
            @keyframes fadeOut {
              from { opacity: 1; }
              to { opacity: 0; }
            }
            
            .loading-fadeout {
              animation: fadeOut 0.5s ease-out forwards;
            } 
          `}
        </style>
      </Container>

      {/* Only render CAPModal if we have all required data */}
      {checklist && auditorData && (
        <ReauditCAPModal
          show={showCAPModal}
          onHide={() => setShowCAPModal(false)}
          capQuestions={capQuestions}
          onSave={handleSaveCAP}
          checklist={checklist}
          auditorData={auditorData}
        />
      )}
    </>
  );
};

export default ReAuditDetails;